import { cva } from 'class-variance-authority';
import { WithChildren, WithDisabled } from '../../../types/component.types';
import { Text } from '../../Text/Text';
import { InputLabel } from '../InputLabel';
import { TextField, TextFieldProps } from '../TextField/TextField';

const selectorStyles = cva(
  'flex flex-col transition rounded-s ring-primary focus-within:ring-3 group',
  {
    variants: {
      intent: {
        default:
          'bg-surface hover:bg-hover-opacity hover:border-strong active:bg-surface-active border [&:has([data-state=checked])]:border-2 [&:has([data-state=checked])]:border-accent',
        disabled: 'bg-surface-disabled text-content-disabled',
      },
    },
    defaultVariants: {
      intent: 'default',
    },
  },
);

const checkboxStyles = cva('flex-1 flex items-center space-x-s p-m', {
  variants: {
    intent: {
      default: 'cursor-pointer',
      disabled: 'cursor-not-allowed',
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});

const freeTextStyles = cva(
  'px-m pb-m flex-col hidden group-[&:has([data-state=checked])]:flex space-y-xxs',
);

type SelectorProps = WithChildren &
  WithDisabled & {
    childId: string;
    body: string | React.ReactNode;
    header?: string;
    freeText?: TextFieldProps;
  };

export const Selector = ({
  childId,
  disabled,
  header,
  body,
  freeText,
  children,
}: SelectorProps) => {
  const { label: freeTextLabel, ...freeTextInputProps } = freeText ?? {};
  const intent = disabled ? 'disabled' : 'default';
  const bodyColor = () => {
    if (disabled) {
      return 'contentDisabled';
    }

    return header ? 'contentSubdued' : 'contentDefault';
  };

  return (
    <div className={selectorStyles({ intent })}>
      <label htmlFor={childId} className={checkboxStyles({ intent })}>
        {children}
        <div>
          {header && (
            <Text size="headingXS" select="none">
              {header}
            </Text>
          )}
          <Text
            size="bodyS"
            select="none"
            color={bodyColor()}
            {...(typeof body !== 'string' && { as: 'div' })}
          >
            {body}
          </Text>
        </div>
      </label>
      {freeText && (
        <div className={freeTextStyles()}>
          <InputLabel htmlFor={`${childId}-custom`}>{freeTextLabel}</InputLabel>
          <TextField
            {...freeTextInputProps}
            id={`${childId}-custom`}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
